const _excluded = ["isUnderReview", "modelClass", "modelId", "modelField", "editorInitConfig", "trackChangesOnCreateComment", "trackChangesEditoInitFn", "onEditorChange", "getContentFn", "updateContentFn", "onUpdateContentFn", "onInitialContentLoad", "showTrackChangesControls"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import debounce from 'lodash.debounce';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { handleError } from '../../store/features/alertSlice';
import ReviewableFieldEditor from './ReviewableFieldEditor';

/**
 * Loads the ReviewableFieldEditor
 * Accepts callbacks for fetching and updating the backend model data.
 * Uses lodash debounce to throttle the updates.
 * Makes it so that the user can write, and the backend will be updated automatically
 * as the user writes.
 * 
 * This component has additional functionality to handle the case where the user may write a bit,
 * take a break, and write again, and thus trigger several updates to the backend.
 * There is a potential race condition, which may happen if the user continues to write,
 * but the backend responds with the result of a previous update. Should we then update the
 * content of the editor with the result from the backend? - No.
 * This component handles that case, by saving a timestamp of each content edit.
 * Thus it can check whether the user has made any edits, after the latest request to the backend.
 * If yes - we disregard the backend data.
 * If no - we use the backend data.
 * However there is a catch - we use Nokogiri at the backend, and Nokogiri creates
 * unintended consequences. Therefore this functionality is currently disabled.
 * @returns 
 */
export default /*#__PURE__*/memo(function ReviewableFieldEditorWithAutomaticUpdates(_ref) {
  let {
      isUnderReview,
      modelClass,
      modelId,
      modelField,
      editorInitConfig,
      trackChangesOnCreateComment,
      trackChangesEditoInitFn,
      onEditorChange,
      getContentFn,
      updateContentFn,
      onUpdateContentFn,
      onInitialContentLoad,
      showTrackChangesControls
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const [oldText, setOldText] = useState(null);
  const [editText, setEditText] = useState({});
  const [apiUpdateResult, setApiUpdateResult] = useState({});
  const dispatch = useDispatch();
  const [originalContent, setOriginalContent] = useState(null);
  const setEditData = useCallback(newEditText => {
    setEditText(newEditText);
  }, []);

  /**
   * Fetches the initial content.
   * Sets the initial time of update to null.
   */
  useEffect(() => {
    const setText = async () => {
      try {
        const content = await getContentFn();
        const initialEditText = {
          content: content,
          timeOfUpdate: null
        };
        setEditData(initialEditText);
        setOriginalContent(content);
        if (onInitialContentLoad) {
          await onInitialContentLoad();
        }
      } catch (error) {
        dispatch(handleError(error));
      }
    };
    setText();
  }, [dispatch, getContentFn, onInitialContentLoad, setEditData]);

  /**
   * Uses lodash debounce to debounce the update call.
   * Uses the passed timeOfUpdate to set the apiUpdateResult
   * In the next useEffect, if the apiUpdateResult's timeOfUpdate is greater or equal to the
   * prior textData's timeOfUpdate, then the api result text is used. Otherwise it is discarded.
   */
  const debouncedUpdateModel = useMemo(() => {
    const updateModel = async (text, timeOfUpdate) => {
      await updateContentFn(text);
      /**
       * 
       * The below code is commented out, because the backend uses Nokogiri.
       * Nokogiri is incapable of not changing the HTML it receives.
       * First it tries to pretty print it by default, and all Nokogiri methods
       * add random new lines here and there. This is manageable, but it is 
       * unfortunately impossible to make nokogiri NOT change &nbsp; to ' '.
       * There does not seem to be any way to inform Nokogiri to not do the
       * additional work.
       * 
       * Until we find a way to fix this, we cannot rely on the backend to not 
       * mangle the html.
       * 
       * Example 1:
       * E.g.: (Notice that we do not change anything.)
       * # a = '<div>asd<span>cvb</span></div>'     <--- initial
       * # doc = Nokogiri::HTML.fragment(a)
       * # doc.to_s
       * # => "<div>asd<span>cvb</span>\n</div>"    <--- output
       * # NOTICE the additional \n in the output.
       * 
       * Example 2:
       * # E.g.: by default Nokogiri replaces &nbsp with ' '.
       * # E.g.: 
       * #    a = '<div>asd<span>cv&nbsp;b</span></div>'
       * #    doc = Nokogiri::HTML.fragment(a)
       * #    puts doc.to_html
       * #    => "<div>asd<span>cv b</span>\n</div>"   # <--- notice the added \n and the added ' '
       * #    doc.to_html(save_with: Nokogiri::XML::Node::SaveOptions.new) # this makes it so that Nokogiri does not randomly add \n characters.
       * #    puts doc.to_html
       * #    => "<div>asd<span>cv b</span></div>"   # <--- now we don't have the \n, but we still have the ' '
       * 
       * Note: https://stackoverflow.com/questions/7756123/nokogiri-leaving-html-entities-untouched
       */
      // const updatedContent = await updateContentFn(text)

      // setApiUpdateResult({
      //   editDataParams: {
      //     content: updatedContent
      //   },
      //   timeOfUpdate
      // })
    };
    return debounce(async (text, timeOfUpdate) => {
      try {
        await updateModel(text, timeOfUpdate);
        if (onUpdateContentFn) {
          await onUpdateContentFn(text);
        }
      } catch (error) {
        console.log(error);
        dispatch(handleError(error));
      }
    }, 1000);
  }, [dispatch, onUpdateContentFn, updateContentFn]);

  /**
   * We generally want to show the user the same content that we have on the backend.
   * However if we always update the content as the user types, even if we debounce,
   * we may have a case where the backend takes too long ot respond, the user types, and loses his changes.
   * E.g.
   * Original content: text
   * User edit: tex
   * -> Backend is called with request 1, content: tex
   * User edit: te123
   * -> Backend is called with request 2, content: te123
   * -> Backend responds with response 2: content: te123
   * -> Backend responds with response 1: content: tex
   * Final content: tex
   */
  useEffect(() => {
    if (!oldText && !editText.content && !apiUpdateResult.editParams) {
      // This use effect is called once when the component is first rendered.
      // There is no need for this call, so we skip it. This is done in order to prevent needless updates that can otherwise cause issues.
      return;
    } else if (!editText.timeOfUpdate && !apiUpdateResult.timeOfUpdate) {
      // Here we have loaded the initial data and set it up.
      // But there is still no reason to call this use effect.
      return;
    }
    if (editText.timeOfUpdate) {
      if (editText.timeOfUpdate > apiUpdateResult.timeOfUpdate || !apiUpdateResult.timeOfUpdate) {
        debouncedUpdateModel(editText.content, editText.timeOfUpdate);
      } else if (apiUpdateResult.timeOfUpdate && apiUpdateResult.timeOfUpdate >= editText.timeOfUpdate) {
        setEditData(_objectSpread({}, apiUpdateResult.editDataParams));
      }
    }
  }, [apiUpdateResult, debouncedUpdateModel, editText, oldText, originalContent, setEditData]);

  // We need to have everything as callback, because every function that we do not pass as a callback
  // gets redefined on every render cycle - triggering a re-render of any child components.
  // Even if the function is an empty function like so: () => {}
  const onTrackChangesCreateComment = useCallback(content => {
    if (trackChangesOnCreateComment) {
      trackChangesOnCreateComment();
    }
    const timeOfUpdate = Date.now();
    setEditText({
      content,
      timeOfUpdate
    });
  }, [trackChangesOnCreateComment]);
  const onEditorChangeCallback = useCallback(content => {
    if (onEditorChange) {
      onEditorChange();
    }
    const timeOfUpdate = Date.now();
    setOldText(editText === null || editText === void 0 ? void 0 : editText.content);
    setEditText({
      content,
      timeOfUpdate
    });
  }, [editText === null || editText === void 0 ? void 0 : editText.content, onEditorChange]);
  return /*#__PURE__*/React.createElement(ReviewableFieldEditor, _extends({
    isUnderReview: isUnderReview,
    editorContent: editText.content,
    modelClass: modelClass,
    modelId: modelId,
    modelField: modelField,
    editorInitConfig: editorInitConfig,
    trackChangesOnCreateComment: onTrackChangesCreateComment,
    trackChangesEditoInitFn: trackChangesEditoInitFn,
    onEditorChange: onEditorChangeCallback,
    showTrackChangesControls: showTrackChangesControls
  }, props));
});